import React, { useContext, useRef, useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { Button, Tooltip, OverlayTrigger, Modal, Badge, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { PermissionContext } from "../permission/PermissionContext";
import axiosInstance from "../../../services/AxiosInstance";
import notify from "../../../utils/notification";
import swal from "sweetalert";

const ExcelUsers = ({ onSwitchToManual }) => {
  const navigate = useNavigate();
  const { sports = [] } = useContext(PermissionContext) || {};
  const fileInputRef = useRef(null);

  const [data, setData] = useState([]);
  const [parentUserTypes, setParentUserTypes] = useState([]);
  const [allChildUserTypes, setAllChildUserTypes] = useState([]);
  const [filteredChildUserTypes, setFilteredChildUserTypes] = useState([]);

  const [selectedParentCategory, setSelectedParentCategory] = useState("");
  const [selectedChildCategory, setSelectedChildCategory] = useState("");
  const [selectedSport, setSelectedSport] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [uploadedFileSize, setUploadedFileSize] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");

  // Fetch Parent User Types and Child User Types on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Fetch Parent User Types (UserCategory)
        let parentList = [];
        try {
          const parentRes = await axiosInstance.get("/user-type/all?page=1&limit=200");
          parentList = parentRes.data?.data || parentRes.data || [];
        } catch (e) {
          const fallbackParent = await axiosInstance.get("/user-type");
          parentList = fallbackParent.data?.data || fallbackParent.data || [];
        }
        setParentUserTypes(parentList);

        // Fetch Child User Types (UserCategorySubType)
        let childList = [];
        try {
          const childRes = await axiosInstance.get("/user-sub-type/all?page=1&limit=500");
          childList = childRes.data?.data || childRes.data || [];
        } catch (e) {
          const fallbackChild = await axiosInstance.get("/user-sub-type");
          childList = fallbackChild.data?.data || fallbackChild.data || [];
        }
        setAllChildUserTypes(childList);
      } catch (err) {
        console.error("Error loading user types:", err);
      }
    };

    fetchCategories();
  }, []);

  // Handle Parent Category Change -> dynamically filter Child Categories
  const handleParentCategoryChange = (e) => {
    const parentId = e.target.value;
    setSelectedParentCategory(parentId);

    if (!parentId) {
      setFilteredChildUserTypes([]);
      setSelectedChildCategory("");
      return;
    }

    const matchingChildren = allChildUserTypes.filter(
      (item) => String(item.parent_category_id) === String(parentId)
    );
    setFilteredChildUserTypes(matchingChildren);

    // Require explicit manual selection of Child User Type (no auto-selection)
    setSelectedChildCategory("");
  };

  const resetInput = () => {
    setData([]);
    setUploadedFileName("");
    setUploadedFileSize("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Generate and download sample template dynamically
  const handleDownloadSample = (type = "xlsx") => {
    const sampleData = [
      {
        "First Name": "Rahul",
        "Middle Name": "Kumar",
        "Last Name": "Sharma",
        "Email": "rahul.sharma@example.com",
        "Mobile": "9876543210",
        "Aadhar Number": "123456789012",
        "Gender": "Male",
        "Designation": "Athlete",
        "Responsible Organization": "Delhi Sports Club",
        "Photo URL (Recommended: 480x640 px, Max 2MB)": "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=400",
      },
      {
        "First Name": "Pooja",
        "Middle Name": "",
        "Last Name": "Verma",
        "Email": "pooja.verma@example.com",
        "Mobile": "9876543211",
        "Aadhar Number": "123456789013",
        "Gender": "Female",
        "Designation": "Official",
        "Responsible Organization": "MCD",
        "Photo URL (Recommended: 480x640 px, Max 2MB)": "",
      },
      {
        "First Name": "Amit",
        "Middle Name": "Singh",
        "Last Name": "Rawat",
        "Email": "amit.rawat@example.com",
        "Mobile": "9876543212",
        "Aadhar Number": "123456789014",
        "Gender": "Male",
        "Designation": "Volunteer",
        "Responsible Organization": "Delhi League",
        "Photo URL (Recommended: 480x640 px, Max 2MB)": "https://drive.google.com/file/d/1exampleFileID/view?usp=sharing",
      },
    ];

    const photoGuidelines = [
      { "Parameter": "Recommended Dimensions", "Specification": "480 x 640 pixels (3:4 passport badge ratio)" },
      { "Parameter": "Recommended File Size", "Specification": "Under 2 MB (System auto-resizes & compresses to ~40KB)" },
      { "Parameter": "Accepted Formats", "Specification": "JPEG, JPG, PNG, WebP" },
      { "Parameter": "Supported Link Types", "Specification": "Direct Image URL (HTTPS) or Google Drive Public Link" },
      { "Parameter": "Google Drive Permission", "Specification": "Must be set to 'Anyone with the link can view'" },
      { "Parameter": "Auto Resizing", "Specification": "Yes, system automatically resizes all photos on import" },
      { "Parameter": "Is Photo Mandatory?", "Specification": "Optional (can be uploaded later via Edit User screen)" },
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");

    if (type === "xlsx") {
      const wsGuide = XLSX.utils.json_to_sheet(photoGuidelines);
      XLSX.utils.book_append_sheet(wb, wsGuide, "Photo Guidelines");
    }

    if (type === "csv") {
      XLSX.writeFile(wb, "Sample_Users_Template.csv", { bookType: "csv" });
    } else {
      XLSX.writeFile(wb, "Sample_Users_Template.xlsx", { bookType: "xlsx" });
    }
  };

  // Parse uploaded Excel / CSV file
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".xls") && !fileName.endsWith(".csv")) {
      swal("Invalid File Format", "Please upload a valid Excel (.xlsx, .xls) or CSV (.csv) file.", "warning");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploadedFileName(file.name);
    setUploadedFileSize((file.size / 1024).toFixed(1) + " KB");

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const rawData = new Uint8Array(event.target.result);
        const workbook = XLSX.read(rawData, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (!jsonData || jsonData.length === 0) {
          swal("Empty File", "The uploaded sheet contains no data rows.", "warning");
          resetInput();
          return;
        }

        const parsedRows = jsonData.map((item, index) => {
          const errors = [];
          const firstName = (item["First Name"] || item["firstname"] || item["First_Name"] || "").toString().trim();
          const middleName = (item["Middle Name"] || item["middlename"] || item["Middle_Name"] || "").toString().trim();
          const lastName = (item["Last Name"] || item["lastname"] || item["Last_Name"] || "").toString().trim();
          const email = (item["Email"] || item["email"] || item["Email_ID"] || item["Email ID"] || "").toString().trim();
          const mobile = (item["Mobile"] || item["mobile"] || item["Phone"] || item["phone"] || "").toString().trim();
          const aadharNumber = (item["Aadhar Number"] || item["Aadhar"] || item["aadhar"] || item["Aadhar_Number"] || item["id_proof_number"] || "").toString().trim();
          const rawGender = (item["Gender"] || item["gender"] || "").toString().trim();
          const designation = (item["Designation"] || item["designation"] || "").toString().trim();
          const responsibleOrganization = (item["Responsible Organization"] || item["Organization"] || item["organization"] || "").toString().trim();
          const photoKey = Object.keys(item).find((k) => {
            const lower = k.toLowerCase();
            return lower.includes("photo") || lower.includes("image");
          });
          const rawPhoto = (
            (photoKey ? item[photoKey] : "") ||
            item["Photo URL"] ||
            item["Photo Link"] ||
            item["Photo"] ||
            item["photo"] ||
            item["Image URL"] ||
            item["photo_url"] ||
            item["PhotoUrl"] ||
            ""
          ).toString().trim();

          if (!firstName) errors.push("First Name required");
          if (!lastName) errors.push("Last Name required");
          if (!email) {
            errors.push("Email required");
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            errors.push("Invalid email format");
          }

          const cleanMobile = mobile.replace(/[^0-9]/g, "");
          if (!cleanMobile) {
            errors.push("Mobile required");
          } else if (cleanMobile.length !== 10) {
            errors.push("Mobile must be 10 digits");
          }

          const cleanAadhar = aadharNumber ? aadharNumber.replace(/[^0-9]/g, "") : "";
          if (cleanAadhar && cleanAadhar.length !== 12) {
            errors.push("Aadhar must be 12 digits");
          }

          let photoUrl = "";
          let photoWarning = "";
          const emptyPlaceholders = [
            "",
            "na",
            "n/a",
            "-",
            "--",
            "none",
            "nil",
            "null",
            "no",
            "undefined",
            "n.a.",
          ];

          if (rawPhoto) {
            let cleaned = rawPhoto.trim();
            if (emptyPlaceholders.includes(cleaned.toLowerCase())) {
              cleaned = "";
            } else {
              // Auto-prefix protocol if missing (e.g. drive.google.com/..., www...., or domain.com/...)
              if (!cleaned.startsWith("http://") && !cleaned.startsWith("https://")) {
                if (cleaned.startsWith("//")) {
                  cleaned = "https:" + cleaned;
                } else if (
                  cleaned.startsWith("drive.google.com") ||
                  cleaned.startsWith("www.") ||
                  /^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/.*)?$/i.test(cleaned)
                ) {
                  cleaned = "https://" + cleaned;
                }
              }

              if (cleaned.startsWith("http://") || cleaned.startsWith("https://")) {
                photoUrl = cleaned;
              } else {
                // If it's a filename like vinit.png or invalid text, do NOT block the entire user!
                // Photo is optional: record warning for tooltip badge, but leave errors empty so user is valid.
                photoWarning = `Photo skipped: '${rawPhoto}' is not a public web URL. Provide a public link (https://).`;
              }
            }
          }

          let normalizedGender = 1;
          const gLower = rawGender.toLowerCase();
          if (gLower === "female" || gLower === "f" || gLower === "2") {
            normalizedGender = 2;
          } else {
            normalizedGender = 1;
          }

          return {
            sr_no: index + 1,
            firstName,
            middleName,
            lastName,
            email,
            mobile: cleanMobile,
            aadharNumber: cleanAadhar,
            gender: normalizedGender,
            genderLabel: normalizedGender === 2 ? "Female" : "Male",
            designation,
            responsibleOrganization,
            photoUrl,
            rawPhoto,
            photoWarning,
            errors,
            isValid: errors.length === 0,
          };
        });

        setData(parsedRows);
      } catch (err) {
        console.error("Error reading file:", err);
        swal("Read Error", "Failed to parse the file. Please verify the file structure.", "error");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const validRowCount = data.filter((row) => row.isValid).length;
  const errorRowCount = data.length - validRowCount;

  const currentParentName =
    parentUserTypes.find((p) => String(p.category_id || p.id) === String(selectedParentCategory))
      ?.category_name_view ||
    parentUserTypes.find((p) => String(p.category_id || p.id) === String(selectedParentCategory))
      ?.category_name ||
    "";

  const currentChildName =
    allChildUserTypes.find((c) => String(c.sub_category_id || c.id) === String(selectedChildCategory))
      ?.sub_category_name_view ||
    allChildUserTypes.find((c) => String(c.sub_category_id || c.id) === String(selectedChildCategory))
      ?.sub_category_name ||
    "";

  const parseDuplicatesResponse = (resObj) => {
    if (!resObj) return { dupEmails: [], dupAadhars: [], isDuplicate: false, message: "" };

    const dupEmails =
      resObj?.error?.duplicateEmails ||
      resObj?.data?.duplicateEmails ||
      resObj?.message?.duplicateEmails ||
      (Array.isArray(resObj?.duplicateEmails) ? resObj.duplicateEmails : []) ||
      [];

    const dupAadhars =
      resObj?.error?.duplicateAadhars ||
      resObj?.data?.duplicateAadhars ||
      resObj?.message?.duplicateAadhars ||
      (Array.isArray(resObj?.duplicateAadhars) ? resObj.duplicateAadhars : []) ||
      [];

    let rawMessage = "";
    if (typeof resObj?.message === "string" && resObj.message.trim()) {
      rawMessage = resObj.message.trim();
    } else if (typeof resObj?.statusCode === "string" && resObj.statusCode.trim()) {
      rawMessage = resObj.statusCode.trim();
    } else if (typeof resObj?.error === "string" && resObj.error.trim()) {
      rawMessage = resObj.error.trim();
    } else if (typeof resObj?.error?.message === "string" && resObj.error.message.trim()) {
      rawMessage = resObj.error.message.trim();
    }

    const isDuplicate =
      dupEmails.length > 0 ||
      dupAadhars.length > 0 ||
      (Boolean(rawMessage) && rawMessage.toLowerCase().includes("duplicate"));

    return { dupEmails, dupAadhars, isDuplicate, rawMessage };
  };

  // Handle Bulk Submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedParentCategory) {
      swal("Select Parent User Type", "Please select a Parent User Type first.", "warning");
      return;
    }

    if (!selectedChildCategory) {
      swal("Select Child User Type", "Please select a Child User Type (Sub-category) before submitting.", "warning");
      return;
    }

    if (!data || data.length === 0) {
      swal("No Data", "Please select an Excel or CSV file to import users.", "warning");
      return;
    }

    const invalidRows = data.filter((row) => !row.isValid);
    if (invalidRows.length > 0) {
      setModalTitle("Validation Errors Detected");
      setModalMessage(
        `Please fix the ${invalidRows.length} invalid row(s) before uploading:\n\n` +
          invalidRows
            .slice(0, 15)
            .map((r) => `Row ${r.sr_no} (${r.firstName || "N/A"} ${r.lastName || ""}): ${r.errors.join(", ")}`)
            .join("\n") +
          (invalidRows.length > 15 ? `\n\n...and ${invalidRows.length - 15} more rows with errors.` : "")
      );
      setShowModal(true);
      return;
    }

    // Check for duplicate emails or Aadhar numbers inside the uploaded file itself
    const seenEmails = new Set();
    const sheetDuplicateEmails = [];
    const seenAadhars = new Set();
    const sheetDuplicateAadhars = [];

    data.forEach((row) => {
      if (row.email) {
        const lowerEmail = String(row.email).toLowerCase().trim();
        if (seenEmails.has(lowerEmail)) {
          sheetDuplicateEmails.push(`Row ${row.sr_no}: ${row.email}`);
        } else {
          seenEmails.add(lowerEmail);
        }
      }
      if (row.aadharNumber) {
        const aadharStr = String(row.aadharNumber).trim();
        if (seenAadhars.has(aadharStr)) {
          sheetDuplicateAadhars.push(`Row ${row.sr_no}: ${row.aadharNumber}`);
        } else {
          seenAadhars.add(aadharStr);
        }
      }
    });

    if (sheetDuplicateEmails.length > 0 || sheetDuplicateAadhars.length > 0) {
      const summaryMsg = [...sheetDuplicateEmails, ...sheetDuplicateAadhars].slice(0, 2).join(", ");
      notify("error", `Duplicate entry in file: ${summaryMsg}`);
      setModalTitle("Duplicate Records in File");
      setModalMessage(
        "Your uploaded file contains duplicate entries within itself:\n\n" +
        [...sheetDuplicateEmails, ...sheetDuplicateAadhars].join("\n")
      );
      setShowModal(true);
      return;
    }

    setIsSubmitting(true);
    const dataForSubmission = data.map((item) => ({
      firstName: item.firstName,
      middleName: item.middleName || null,
      lastName: item.lastName,
      email: item.email,
      mobile: item.mobile,
      sub_category_type: selectedChildCategory,
      gender: item.gender,
      sportId: selectedSport || null,
      responsibleOrganization: item.responsibleOrganization || null,
      designation: item.designation || null,
      aadharNumber: item.aadharNumber || null,
      photo: item.photoUrl || null,
      officeIDImage: null,
      sr_no: item.sr_no,
    }));

    try {
      const response = await axiosInstance.post("/user/bulk-candidate", dataForSubmission);
      const res = response.data;

      const isSuccess =
        res?.success === true ||
        (res?.success !== false && (res?.statusCode === 200 || res?.status === 200));

      if (isSuccess) {
        notify("success", `Successfully imported ${dataForSubmission.length} candidate(s)!`);
        swal({
          title: "Import Successful!",
          text: `Successfully imported ${dataForSubmission.length} candidate(s) under ${currentParentName} > ${currentChildName}.`,
          icon: "success",
          buttons: {
            viewUsers: { text: "Go to Users List", value: "list" },
            importMore: { text: "Import More", value: "more" },
          },
        }).then((value) => {
          resetInput();
          if (value === "list") {
            navigate("/approve-users");
          }
        });
      } else {
        const { dupEmails, dupAadhars, isDuplicate, rawMessage } = parseDuplicatesResponse(res);

        if (isDuplicate) {
          const toastSummary = [
            ...dupEmails.map((d) => `Email: ${d.email || d}${d.sr_no ? ` (Row ${d.sr_no})` : ""}`),
            ...dupAadhars.map((d) => `Aadhar: ${d.aadharNumber || d}${d.sr_no ? ` (Row ${d.sr_no})` : ""}`),
          ].slice(0, 2).join(", ");

          notify("error", `Duplicate entry: ${toastSummary || rawMessage || "Records already exist in database"}`);

          let msg = "";
          if (dupEmails.length) {
            msg += "The following email addresses already exist in the database:\n\n" +
              dupEmails.map((d) => `• Row ${d.sr_no || "?"}: ${d.email || d}`).join("\n");
          }
          if (dupAadhars.length) {
            msg += (msg ? "\n\n" : "") + "The following Aadhar numbers already exist in the database:\n\n" +
              dupAadhars.map((d) => `• Row ${d.sr_no || "?"}: ${d.aadharNumber || d}`).join("\n");
          }
          setModalTitle("Duplicate Records Detected");
          setModalMessage(msg || rawMessage || "Records already exist in database.");
          setShowModal(true);
        } else {
          notify("error", rawMessage || "Bulk user creation failed. Please check inputs.");
        }
      }
    } catch (error) {
      console.error("Error submitting bulk data:", error);
      const errData = error.response?.data;
      const { dupEmails, dupAadhars, isDuplicate, rawMessage } = parseDuplicatesResponse(errData);

      if (isDuplicate) {
        const toastSummary = [
          ...dupEmails.map((d) => `Email: ${d.email || d}${d.sr_no ? ` (Row ${d.sr_no})` : ""}`),
          ...dupAadhars.map((d) => `Aadhar: ${d.aadharNumber || d}${d.sr_no ? ` (Row ${d.sr_no})` : ""}`),
        ].slice(0, 2).join(", ");

        notify("error", `Duplicate entry: ${toastSummary || rawMessage || "Records already exist in database"}`);

        let msg = "";
        if (dupEmails.length) {
          msg += "The following email addresses already exist in the database:\n\n" +
            dupEmails.map((d) => `• Row ${d.sr_no || "?"}: ${d.email || d}`).join("\n");
        }
        if (dupAadhars.length) {
          msg += (msg ? "\n\n" : "") + "The following Aadhar numbers already exist in the database:\n\n" +
            dupAadhars.map((d) => `• Row ${d.sr_no || "?"}: ${d.aadharNumber || d}`).join("\n");
        }
        setModalTitle("Duplicate Records Detected");
        setModalMessage(msg || rawMessage || "Records already exist in database.");
        setShowModal(true);
      } else {
        const errMsg = rawMessage || error.message || "Failed to submit bulk users";
        notify("error", errMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card shadow-sm border-0">
      <div className="card-header bg-white border-bottom py-3 d-flex flex-wrap align-items-center justify-content-between">
        <div>
          <h4 className="card-title mb-1 text-primary">
            <i className="fa fa-file-excel me-2"></i>
            Bulk Import Users (Excel / CSV)
          </h4>
          <p className="text-muted small mb-0">
            Select Parent User Type, then choose Child User Type, upload candidate records, and click save.
          </p>
        </div>
        <div className="d-flex gap-2 mt-2 mt-sm-0">
          <Button
            variant="outline-success"
            size="sm"
            onClick={() => handleDownloadSample("xlsx")}
            className="d-flex align-items-center"
          >
            <i className="fa fa-download me-1"></i> Sample Excel (.xlsx)
          </Button>
          <Button
            variant="outline-info"
            size="sm"
            onClick={() => handleDownloadSample("csv")}
            className="d-flex align-items-center"
          >
            <i className="fa fa-file-csv me-1"></i> Sample CSV (.csv)
          </Button>
          {onSwitchToManual && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onSwitchToManual}
            >
              <i className="fa fa-user-plus me-1"></i> Single User Form
            </Button>
          )}
        </div>
        <div className="w-100 mt-2">
          <div className="alert alert-info py-2 px-3 mb-0 small d-flex align-items-center">
            <i className="fa fa-camera me-2 fs-6"></i>
            <div>
              <strong>Photograph Recommendation:</strong> Ideal dimensions: <strong>480 × 640 px</strong> (Passport 3:4 ratio), size under 2MB, JPG/PNG or Public Google Drive link. <em>(System automatically resizes and compresses all imported photos).</em>
            </div>
          </div>
        </div>
      </div>

      <div className="card-body">
        {/* Step 1: Configuration Form */}
        <div className="bg-light p-3 rounded mb-4 border">
          <div className="row g-3 align-items-end">
            {/* 1. Parent User Type Selection */}
            <div className="col-md-3">
              <label className="form-label fw-bold small text-dark mb-1">
                Parent User Type <span className="text-danger">*</span>
              </label>
              <select
                className="form-select form-control"
                value={selectedParentCategory}
                onChange={handleParentCategoryChange}
                required
              >
                <option value="">-- Choose Parent Type --</option>
                {parentUserTypes.map((type) => (
                  <option
                    key={type.category_id || type.id}
                    value={type.category_id || type.id}
                  >
                    {type.category_name_view || type.category_name || `Category #${type.category_id}`}
                  </option>
                ))}
              </select>
            </div>

            {/* 2. Child User Type Selection (Filtered by Parent) */}
            <div className="col-md-3">
              <label className="form-label fw-bold small text-dark mb-1">
                Child User Type (Sub-Category) <span className="text-danger">*</span>
              </label>
              <select
                className="form-select form-control"
                value={selectedChildCategory}
                onChange={(e) => setSelectedChildCategory(e.target.value)}
                disabled={!selectedParentCategory || filteredChildUserTypes.length === 0}
                required
              >
                <option value="">
                  {!selectedParentCategory
                    ? "-- Select Parent Type First --"
                    : filteredChildUserTypes.length === 0
                    ? "-- No Sub-Types Found --"
                    : "-- Choose Child Type --"}
                </option>
                {filteredChildUserTypes.map((type) => (
                  <option
                    key={type.sub_category_id || type.id}
                    value={type.sub_category_id || type.id}
                  >
                    {type.sub_category_name_view || type.sub_category_name || `Sub-Type #${type.sub_category_id}`}
                  </option>
                ))}
              </select>
            </div>

            {/* 3. Sport Selection (Optional) */}
            <div className="col-md-2">
              <label className="form-label fw-bold small text-dark mb-1">
                Sport <span className="text-muted">(Optional)</span>
              </label>
              <select
                className="form-select form-control"
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
              >
                <option value="">-- Choose Sport --</option>
                {sports.map((sport) => (
                  <option key={sport.sport_id} value={sport.sport_id}>
                    {sport.sport_name}
                  </option>
                ))}
              </select>
            </div>

            {/* 4. Choose File */}
            <div className="col-md-4">
              <label className="form-label fw-bold small text-dark mb-1">
                Upload Excel or CSV File <span className="text-danger">*</span>
              </label>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="category-tooltip" hidden={!!selectedChildCategory}>
                    {!selectedParentCategory
                      ? "Select Parent User Type first"
                      : "Select Child User Type first"}
                  </Tooltip>
                }
              >
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="form-control"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFileChange}
                    disabled={!selectedChildCategory}
                  />
                </div>
              </OverlayTrigger>
            </div>
          </div>

          {/* Active Category Badges */}
          {selectedParentCategory && (
            <div className="mt-3 d-flex flex-wrap align-items-center gap-2 pt-2 border-top">
              <span className="small text-muted fw-bold">Active Selection:</span>
              <Badge bg="primary" className="p-2">
                <i className="fa fa-folder-open me-1"></i> Parent: {currentParentName}
              </Badge>
              {selectedChildCategory ? (
                <Badge bg="success" className="p-2">
                  <i className="fa fa-tag me-1"></i> Child: {currentChildName}
                </Badge>
              ) : (
                <Badge bg="warning" text="dark" className="p-2">
                  <i className="fa fa-arrow-left me-1"></i> Please select Child User Type
                </Badge>
              )}
            </div>
          )}

          {uploadedFileName && (
            <div className="mt-3 d-flex align-items-center justify-content-between bg-white p-2 px-3 rounded border">
              <div className="d-flex align-items-center gap-2">
                <i className="fa fa-file-alt text-success fs-5"></i>
                <div>
                  <strong>{uploadedFileName}</strong>
                  <span className="text-muted ms-2 small">({uploadedFileSize})</span>
                </div>
              </div>
              <Button variant="outline-danger" size="sm" onClick={resetInput}>
                <i className="fa fa-trash me-1"></i> Remove File
              </Button>
            </div>
          )}
        </div>

        {/* Step 2: Data Preview */}
        {data.length > 0 && (
          <div className="mb-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
              <h5 className="mb-1 text-dark">
                Uploaded Records Preview
                <Badge bg="primary" className="ms-2">
                  Total: {data.length}
                </Badge>
                <Badge bg="success" className="ms-2">
                  Valid: {validRowCount}
                </Badge>
                {errorRowCount > 0 && (
                  <Badge bg="danger" className="ms-2">
                    Needs Attention: {errorRowCount}
                  </Badge>
                )}
              </h5>
              <div className="text-muted small">
                {errorRowCount === 0 ? (
                  <span className="text-success fw-bold">
                    <i className="fa fa-check-circle me-1"></i> All rows are valid and ready to submit under:{" "}
                    <strong>{currentParentName} &gt; {currentChildName}</strong>
                  </span>
                ) : (
                  <span className="text-danger fw-bold">
                    <i className="fa fa-exclamation-triangle me-1"></i> Fix highlighted errors before submitting.
                  </span>
                )}
              </div>
            </div>

            <div className="table-responsive border rounded" style={{ maxHeight: "420px", overflowY: "auto" }}>
              <table className="table table-hover table-striped table-bordered align-middle mb-0">
                <thead className="table-light sticky-top">
                  <tr>
                    <th style={{ width: "60px" }}>#</th>
                    <th>Status</th>
                    <th>Photo</th>
                    <th>User Type</th>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Gender</th>
                    <th>Aadhar</th>
                    <th>Designation</th>
                    <th>Organization</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => {
                    const driveIdMatch = row.photoUrl
                      ? (row.photoUrl.match(/\/file\/d\/([a-zA-Z0-9_-]+)/i) || row.photoUrl.match(/[?&]id=([a-zA-Z0-9_-]+)/i))
                      : null;
                    const previewSrc = driveIdMatch && driveIdMatch[1]
                      ? `https://drive.google.com/thumbnail?id=${driveIdMatch[1]}&sz=w80`
                      : row.photoUrl;

                    return (
                      <tr key={row.sr_no} className={!row.isValid ? "table-danger" : ""}>
                        <td className="text-center">{row.sr_no}</td>
                        <td>
                          {row.isValid ? (
                            <span className="badge bg-success-subtle text-success border border-success">
                              <i className="fa fa-check me-1"></i> Valid
                            </span>
                          ) : (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id={`err-tip-${row.sr_no}`}>{row.errors.join(", ")}</Tooltip>}
                            >
                              <span className="badge bg-danger-subtle text-danger border border-danger cursor-pointer">
                                <i className="fa fa-times me-1"></i> {row.errors[0]}
                              </span>
                            </OverlayTrigger>
                          )}
                        </td>
                        <td>
                          {row.photoUrl ? (
                            <div className="d-flex align-items-center">
                              <img
                                src={previewSrc}
                                alt="Photo"
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                  border: "2px solid #0d6efd",
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.style.display = "none";
                                  const fallback = e.target.parentElement.querySelector(".photo-fallback-badge");
                                  if (fallback) fallback.style.display = "inline-block";
                                }}
                              />
                              <span
                                className="badge bg-info-subtle text-info border photo-fallback-badge"
                                style={{ display: "none", fontSize: "10px" }}
                              >
                                Link Valid
                              </span>
                            </div>
                          ) : row.photoWarning ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id={`photo-warn-${row.sr_no}`}>{row.photoWarning}</Tooltip>}
                            >
                              <span
                                className="badge bg-warning-subtle text-dark border border-warning cursor-pointer"
                                style={{ fontSize: "11px" }}
                              >
                                <i className="fa fa-exclamation-triangle text-warning me-1"></i>
                                {row.rawPhoto && row.rawPhoto.length > 12 ? row.rawPhoto.substring(0, 10) + "..." : row.rawPhoto || "Link Skipped"}
                              </span>
                            </OverlayTrigger>
                          ) : (
                            <span className="badge bg-light text-muted border" style={{ fontSize: "11px" }}>
                              No Photo
                            </span>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            {currentChildName || "N/A"}
                          </span>
                        </td>
                        <td>
                          <strong>{row.firstName}</strong> {row.middleName ? `${row.middleName} ` : ""}
                          {row.lastName}
                        </td>
                        <td>{row.email || <span className="text-muted">N/A</span>}</td>
                        <td>{row.mobile || <span className="text-muted">N/A</span>}</td>
                        <td>{row.genderLabel}</td>
                        <td>{row.aadharNumber || <span className="text-muted">-</span>}</td>
                        <td>{row.designation || <span className="text-muted">-</span>}</td>
                        <td>{row.responsibleOrganization || <span className="text-muted">-</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Step 3: Submission Actions */}
            <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
              <Button variant="secondary" onClick={resetInput} disabled={isSubmitting}>
                Reset
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSubmitting || errorRowCount > 0 || data.length === 0 || !selectedChildCategory}
                className="px-4"
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Submitting Users...
                  </>
                ) : (
                  <>
                    <i className="fa fa-cloud-upload-alt me-2"></i>
                    Save and Import {validRowCount} User{validRowCount !== 1 ? "s" : ""}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Informational Guidance Box */}
        {data.length === 0 && (
          <div className="p-4 border rounded bg-white text-center">
            <i className="fa fa-file-excel text-muted" style={{ fontSize: "48px" }}></i>
            <h5 className="mt-3 text-secondary">No File Uploaded Yet</h5>
            <p className="text-muted mx-auto" style={{ maxWidth: "650px" }}>
              To bulk register candidates:
              <br />
              1. Select your <strong>Parent User Type</strong> (e.g., Media, Official, Athlete).
              <br />
              2. Select the corresponding <strong>Child User Type</strong> (Sub-Category).
              <br />
              3. Download the <strong>Sample Excel</strong> or <strong>Sample CSV</strong> template, enter the candidate information, and upload your file.
            </p>
            <div className="d-flex justify-content-center gap-2 mt-2">
              <Button variant="outline-success" size="sm" onClick={() => handleDownloadSample("xlsx")}>
                <i className="fa fa-download me-1"></i> Download Sample Excel
              </Button>
              <Button variant="outline-info" size="sm" onClick={() => handleDownloadSample("csv")}>
                <i className="fa fa-download me-1"></i> Download Sample CSV
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modal for displaying validation and duplicate details */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="text-danger fs-5">
            <i className="fa fa-exclamation-triangle me-2"></i>
            {modalTitle || "Notification"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-danger" style={{ whiteSpace: "pre-line", maxHeight: "350px", overflowY: "auto" }}>
            {modalMessage}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ExcelUsers;
